// client/src/pages/seo/ChangeMenuOnlineEn.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "start",
    label:
      "A restaurant menu should be as easy to update as daily operations require",
  },
  {
    id: "why",
    label: "Why is it important to change a restaurant menu online?",
  },
  {
    id: "what",
    label: "What should a restaurant be able to change online?",
  },
  {
    id: "workflow",
    label: "How does changing a menu online work in practice?",
  },
  {
    id: "pdf",
    label: "Why is a PDF often inconvenient for frequent menu changes?",
  },
  {
    id: "mistakes",
    label: "What mistakes happen when restaurants update menus online?",
  },
  {
    id: "recommendation",
    label: "How would I approach this as a restaurant owner?",
  },
  {
    id: "next-steps",
    label: "What should you do now?",
  },
  {
    id: "bestplace",
    label: "How Best Place helps you update your menu online",
  },
  {
    id: "faq",
    label: "FAQ about changing a restaurant menu online",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visual idea">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idea for illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function ChangeMenuOnlineEn() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "en");
    document.title =
      "Change Menu Online: Update Prices and Dishes Yourself | Best Place";
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="en"
        langLinks={{
          en: "/en/change-menu-online",
          de: "/de/speisekarte-online-aendern",
          no: "/no/endre-meny-online",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Change your menu online: update prices, dishes and photos yourself
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                In a restaurant, something changes all the time: a dish sells
                out, a price needs an update, a daily special is added or a
                better photo is ready. This is where a digital menu becomes
                practical.
              </p>

              <p className="seoHero__text">
                Instead of exporting a new PDF, asking a designer or reprinting
                the menu, the restaurant can update content online. The QR code
                stays the same, while the menu behind it remains current.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="en" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Content of the article</h2>

        <nav className="qrArticle__nav" aria-label="Article navigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>
            A restaurant menu should be as easy to update as daily operations
            require
          </h2>

          <p>
            Many restaurants still use PDF files or printed menus. That can work
            when little changes. But when prices, availability, daily specials
            or photos change regularly, a static menu quickly becomes
            inconvenient.
          </p>

          <p>
            A digital menu is useful because the restaurant can change content
            online without changing the QR code. This makes everyday updates
            faster, simpler and less dependent on designers, print shops or
            technical help.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Slow workflow</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Write down the change.</li>
                <li className="text__seo-li">
                  Ask a designer or staff member.
                </li>
                <li className="text__seo-li">Create a new PDF.</li>
                <li className="text__seo-li">Upload the file again.</li>
                <li className="text__seo-li">Check the QR code link.</li>
                <li className="text__seo-li">
                  Guests may still see old information.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Better workflow</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Open the admin area.</li>
                <li className="text__seo-li">Edit dish, price or photo.</li>
                <li className="text__seo-li">Save changes.</li>
                <li className="text__seo-li">
                  The digital menu is updated.
                </li>
                <li className="text__seo-li">The QR code stays the same.</li>
              </ul>
            </div>
          </div>

          <h3>Test one small update first</h3>

          <p>
            Add a few real products, change one price and open the menu through
            the QR code on a smartphone. This small test quickly shows whether
            online menu editing can make daily work easier.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Why is it important to change a restaurant menu online?</h2>

            <p>
              A menu is not a static document. It shows not only what the
              restaurant offers, but also whether the information is current and
              reliable for guests.
            </p>

            <p>
              If a guest sees a dish online that is no longer available, it
              creates disappointment. If prices are outdated, it can create
              uncomfortable conversations. If daily specials are not visible,
              opportunities are lost.
            </p>

            <h3>Online updates support daily restaurant operations</h3>

            <p>
              Restaurant technology is most useful when it solves practical
              problems: faster updates, fewer outdated menus, clearer guest
              information and less dependence on printed material.
            </p>

            <p>
              If guests search digitally, scan QR codes and open menus on their
              phones, menu information should also be easy to keep up to date
              online.
            </p>
          </div>
        </section>

        <section id="what" className="acquaintance">
          <h2>What should a restaurant be able to change online?</h2>

          <p>
            A good digital menu should not only display what was entered once.
            It should allow the owner or team to manage the most important
            content directly.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Change prices.</li>
            <li className="text__seo-li">Add dishes.</li>
            <li className="text__seo-li">Hide products.</li>
            <li className="text__seo-li">Publish daily specials.</li>
            <li className="text__seo-li">Replace photos.</li>
            <li className="text__seo-li">Improve descriptions.</li>
            <li className="text__seo-li">Sort categories.</li>
            <li className="text__seo-li">Adjust availability.</li>
          </ul>

          <p>
            The ability to hide products temporarily is especially important. If
            a dish is sold out, it should not stay visible as if it were still
            available.
          </p>

          <VisualIdea>
            An admin screen with three simple actions: change price, hide dish,
            replace photo. Next to it, a smartphone with the updated menu.
          </VisualIdea>
        </section>

        <section id="workflow" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>How does changing a menu online work in practice?</h2>

            <p>
              The workflow should be simple enough that external help is not
              needed for every small change. A restaurant owner, manager or
              staff member should be able to handle typical updates.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">
                <strong>Log in:</strong> The owner opens the admin area.
              </li>

              <li className="text__seo-li">
                <strong>Select item:</strong> Choose a dish, drink or daily
                special.
              </li>

              <li className="text__seo-li">
                <strong>Edit content:</strong> Adjust price, description, photo
                or visibility.
              </li>

              <li className="text__seo-li">
                <strong>Save:</strong> The public menu is updated.
              </li>

              <li className="text__seo-li">
                <strong>Check QR code:</strong> The existing QR code still opens
                the updated menu page.
              </li>
            </ol>

            <p>
              The key point: the QR code does not need to be reprinted as long
              as the menu link stays stable.
            </p>
          </div>
        </section>

        <section id="pdf" className="acquaintance">
          <h2>Why is a PDF often inconvenient for frequent menu changes?</h2>

          <p>
            A PDF feels simple at first. You take the existing menu, export it
            and link it with a QR code. For rare changes, that can be enough.
          </p>

          <p>
            It becomes difficult when the menu changes regularly. Every update
            requires editing, exporting, uploading and testing again.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>PDF menu</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Often hard to read on small screens.
                </li>
                <li className="text__seo-li">
                  Changes require several steps.
                </li>
                <li className="text__seo-li">
                  Old versions can remain in circulation.
                </li>
                <li className="text__seo-li">
                  Not ideal for daily specials.
                </li>
                <li className="text__seo-li">
                  No flexible product management.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Online-managed menu</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">Structured for smartphones.</li>
                <li className="text__seo-li">
                  Prices can be edited directly.
                </li>
                <li className="text__seo-li">
                  Sold-out items can be hidden.
                </li>
                <li className="text__seo-li">
                  Photos and categories are easier to manage.
                </li>
                <li className="text__seo-li">QR code can stay the same.</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What mistakes happen when restaurants update menus online?</h2>

            <p>
              Online updates help only when they are organized clearly.
              Otherwise, even a digital menu can become confusing.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Common mistakes</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Prices are not updated online and offline at the same time.
                  </li>
                  <li className="text__seo-li">
                    Sold-out dishes remain visible.
                  </li>
                  <li className="text__seo-li">
                    Multiple PDF versions circulate.
                  </li>
                  <li className="text__seo-li">
                    Photos are uploaded without a consistent style.
                  </li>
                  <li className="text__seo-li">
                    Categories become too long or messy.
                  </li>
                  <li className="text__seo-li">
                    The QR code points to an old file.
                  </li>
                  <li className="text__seo-li">
                    No one is responsible for menu maintenance.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Better</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Use one central digital menu.
                  </li>
                  <li className="text__seo-li">Save changes immediately.</li>
                  <li className="text__seo-li">
                    Temporarily hide unavailable products.
                  </li>
                  <li className="text__seo-li">
                    Choose photos intentionally.
                  </li>
                  <li className="text__seo-li">
                    Clean up categories regularly.
                  </li>
                  <li className="text__seo-li">Use a stable menu link.</li>
                  <li className="text__seo-li">
                    Assign one responsible person.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="recommendation" className="acquaintance">
          <h2>How would I approach this as a restaurant owner?</h2>

          <p>
            I would not try to digitize the entire menu perfectly at once. I
            would start with the parts that change most often.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Start with daily specials:</strong> The benefit becomes
              visible faster than with a rarely changing menu.
            </li>

            <li className="text__seo-li">
              <strong>Test price updates:</strong> Change one price and check
              whether the update is visible immediately.
            </li>

            <li className="text__seo-li">
              <strong>Hide one dish:</strong> Simulate that a product is sold
              out.
            </li>

            <li className="text__seo-li">
              <strong>Replace a photo:</strong> Check whether images improve
              presentation.
            </li>

            <li className="text__seo-li">
              <strong>Keep the QR code stable:</strong> Continue using the same
              QR code while changing content.
            </li>

            <li className="text__seo-li">
              <strong>Include the team:</strong> At least one person should know
              how to make updates.
            </li>
          </ol>

          <p>
            This makes the digital menu useful in real work, not only as a nice
            online presentation.
          </p>
        </section>

        <section id="next-steps" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What should you do now?</h2>

            <p>
              If you want to change your restaurant menu online, start with a
              small practical test.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Create a digital menu.</li>
              <li className="text__seo-li">Add 10 to 20 real products.</li>
              <li className="text__seo-li">Change one price.</li>
              <li className="text__seo-li">Hide one product as a test.</li>
              <li className="text__seo-li">
                Open the menu through the QR code.
              </li>
              <li className="text__seo-li">
                Check the view on a smartphone.
              </li>
              <li className="text__seo-li">
                Then decide whether to add more categories.
              </li>
            </ul>

            <VisualIdea>
              A simple admin screen where the restaurant owner changes one
              price, hides one sold-out item and immediately checks the updated
              menu on a smartphone.
            </VisualIdea>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="en" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>FAQ about changing a restaurant menu online</h2>

          <div className="seoFaq">
            <details>
              <summary>Can I change menu prices myself online?</summary>
              <p>
                Yes, if the menu is built as a digital menu with an admin area.
                Then prices can be edited and saved directly.
              </p>
            </details>

            <details>
              <summary>
                Do I need to reprint the QR code after every change?
              </summary>
              <p>
                No, if the QR code points to a stable menu link. The menu
                content changes, but the QR code stays the same.
              </p>
            </details>

            <details>
              <summary>Is a PDF menu enough?</summary>
              <p>
                For rare changes, a PDF can be enough. If prices, daily specials
                or availability change more often, an online-managed menu is
                more practical.
              </p>
            </details>

            <details>
              <summary>Can I hide sold-out dishes?</summary>
              <p>
                Yes. That is one of the most useful advantages of a digital
                menu. An item can be hidden temporarily and shown again later.
              </p>
            </details>

            <details>
              <summary>Who should maintain the menu?</summary>
              <p>
                Ideally, someone who knows the daily restaurant flow: the owner,
                manager or responsible staff member.
              </p>
            </details>

            <details>
              <summary>Can I start with only part of the menu?</summary>
              <p>
                Yes. One category, a few real products and a quick QR code test
                are enough for a useful first step.
              </p>
            </details>
          </div>
        </section>
        <section className="articls qrArticle__related">
          <h2>Weitere Themen zur digitalen Speisekarte</h2>
                
          <div className="seoCompareSmall">
            <p>
              <Link to="/en/digital-menu-for-restaurants">
                Digital menu for restaurants
              </Link>
            </p>
                
            <p>
              <Link to="/en/qr-code-menu-for-restaurants">
                Create a QR code menu
              </Link>
            </p>
                
            <p>
              <Link to="/en/free-digital-menu-test">
                Try the digital menu for free
              </Link>
            </p>
                
            <p>
              <Link to="/en/multilingual-menu-for-restaurants">
                Multilingual menu for restaurants
              </Link>
            </p>

            <p>
              <Link to="/en/qr-code-menu-printing">
                Printing of QR codes for restaurant menus
              </Link>
            </p>

            <p>
              <Link to="/en/create-digital-daily-menu">
                Daily update of special offers
              </Link>
            </p>
          </div>
        </section>
      </article>
    </main>
  );
}